import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Users, Key, CheckCircle, XCircle, CreditCard, Brain } from 'lucide-react';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { ErrorMessage } from '../../components/common/ErrorMessage';
import { Badge } from '../../components/common/Badge';
import { organizationApi } from '../../services/adminApi';
import type { Organization } from '../../types/organization';

interface OrganizationListProps {
  onNavigate: (page: string, id?: string) => void;
}

export const OrganizationList: React.FC<OrganizationListProps> = ({ onNavigate }) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const response = await organizationApi.list();
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch organizations');
      }
      
      setOrganizations(response.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to delete organization "${name}"?`)) {
      return;
    }

    try {
      const response = await organizationApi.delete(id);

      if (!response.success) {
        throw new Error(response.message || 'Failed to delete organization');
      }

      await fetchOrganizations();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete organization');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'trial':
        return 'warning';
      case 'suspended':
        return 'error';
      default:
        return 'secondary';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={fetchOrganizations} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Organizations</h1>
          <p className="text-gray-600 mt-1">Manage tenant organizations and their settings</p>
        </div>
        <Button
          onClick={() => onNavigate('admin-organization-add')}
          icon={Plus}
          variant="primary"
        >
          Add Organization
        </Button>
      </div>

      <div className="grid gap-4">
        {organizations.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <Users className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No organizations</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by creating a new organization.</p>
              <div className="mt-6">
                <Button
                  onClick={() => onNavigate('admin-organization-add')}
                  icon={Plus}
                  variant="primary"
                >
                  Add Organization
                </Button>
              </div>
            </div>
          </Card>
        ) : (
          organizations.map((org) => (
            <Card key={org.id} className="hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {org.display_name || org.name}
                    </h3>
                    <Badge variant={getStatusColor(org.status)}>
                      {org.status}
                    </Badge>
                    {org.is_system && (
                      <Badge variant="secondary">System</Badge>
                    )}
                  </div>
                  
                  <p className="text-sm text-gray-600 mt-1">
                    <span className="font-mono">{org.name}</span>
                  </p>
                  
                  {org.description && (
                    <p className="text-gray-600 mt-2">{org.description}</p>
                  )}
                  
                  <div className="flex items-center gap-6 mt-4 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      <span>{org.user_count || 0} / {org.max_users} users</span>
                    </div>
                    
                    {/* Token Status Icons */}
                    <div className="flex items-center gap-3">
                      {org.has_square_token && (
                        <div className="flex items-center gap-1" title={`Square ${org.square_environment} token configured`}>
                          <CreditCard className="h-4 w-4 text-blue-500" />
                          <span className="text-blue-600">Square</span>
                          {org.square_location_id && (
                            <CheckCircle className="h-3 w-3 text-green-500" />
                          )}
                        </div>
                      )}
                      {org.has_ai_token && (
                        <div className="flex items-center gap-1" data-title="AI token configured">
                          <Brain className="h-4 w-4 text-purple-500" />
                          <span className="text-purple-600">AI</span>
                        </div>
                      )}
                      {!org.has_square_token && !org.has_ai_token && (
                        <div className="flex items-center gap-1 text-gray-400">
                          <Key className="h-4 w-4" />
                          <span>No tokens</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-1">
                      {org.is_active ? (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span>Active</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="h-4 w-4 text-red-500" />
                          <span>Inactive</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  {!org.is_system && (
                    <Button
                      onClick={() => onNavigate('admin-organization-ai-config', org.id)}
                      icon={Brain}
                      variant="outline"
                      size="sm"
                      className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 border-purple-200"
                      title={org.has_ai_token ? "Configure AI Prompt" : "Set up AI Configuration"}
                    >
                      Configure AI
                    </Button>
                  )}
                  <Button
                    onClick={() => onNavigate('admin-organization-edit', org.id)}
                    icon={Edit2}
                    variant="ghost"
                    size="sm"
                    title="Edit organization"
                  >
                    <span className="sr-only">Edit</span>
                  </Button>
                  {!org.is_system && (
                    <Button
                      onClick={() => handleDelete(org.id, org.display_name || org.name)}
                      icon={Trash2}
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      title="Delete organization"
                    >
                      <span className="sr-only">Delete</span>
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};